import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty, pick } from 'ramda';

import { setPlayerLimitsInfo } from '@interlayer/js/app/store/reducers/ResponsibleGaming/actions';

import { fetchLimitInfo } from 'js/app/components/main/ResponsibleGaming/server';
import { useResponsibleGamingSelector } from 'js/app/store/hooks/useResponsibleGamingSelector';
import { useDataFetch } from 'js/hooks/useDataFetch';
import { useFormatter } from 'js/hooks/useFormatter';
import { useIsAdmin } from 'js/hooks/user';
import { useUserSelector } from 'js/store/hooks';

export const usePlayerLimitInfo = () => {
    const isAdmin = useIsAdmin();
    const dispatch = useDispatch();
    const { isLoggedIn, paymentsStatistics } = useUserSelector(pick(['isLoggedIn', 'paymentsStatistics']));
    const { date: formatDate } = useFormatter();
    const { playerLimitInfo } = useResponsibleGamingSelector();

    const parseFormatDate = useCallback((date) => {
        if (!date) {
            return null;
        }

        return formatDate(date, '%d/%m/%Y');
    }, [formatDate]);

    const onResponsePlayerLimitInfo = useCallback(({ data }) => {
        const parsedData = {
            ...data,
            isDepositLimits: data.daydepositlimit || data.weekdepositlimit || data.monthdepositlimit,
            depositLimitForm: {
                daydepositlimit: data.daydepositlimit,
                weekdepositlimit: data.weekdepositlimit,
                monthdepositlimit: data.monthdepositlimit
            },
            depositLimitsList: {
                daily: {
                    name: 'daily',
                    limit: data.daydepositlimit,
                    remaining: data.dayremainingdepositlimit,
                    reset: parseFormatDate(data.daydepositlimitperiodstart),
                    pending: data.waitingdaydepositlimit
                },
                weekly: {
                    name: 'weekly',
                    limit: data.weekdepositlimit,
                    remaining: data.weekremainingdepositlimit,
                    reset: parseFormatDate(data.weekdepositlimitperiodstart),
                    pending: data.waitingweekdepositlimit
                },
                monthly: {
                    name: 'monthly',
                    limit: data.monthdepositlimit,
                    remaining: data.monthremainingdepositlimit,
                    reset: parseFormatDate(data.monthdepositlimitperiodstart),
                    pending: data.waitingmonthdepositlimit
                }
            }
        };

        dispatch(setPlayerLimitsInfo(parsedData));

        return parsedData;
    }, [dispatch, parseFormatDate]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const shouldFetch = useCallback(() => isLoggedIn || isAdmin, [isLoggedIn, isAdmin, paymentsStatistics]);

    return useDataFetch({
        initialIsLoading: isEmpty(playerLimitInfo),
        fetch: fetchLimitInfo,
        initialData: playerLimitInfo,
        params: playerLimitInfo,
        shouldFetch,
        onResponse: onResponsePlayerLimitInfo
    });
};
